import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, FileText, CheckCircle, AlertCircle, Trash2 } from "lucide-react";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface FanninCountyTextParserProps {
  onRecordsSaved?: () => void;
}

interface ParsedRecord {
  documentNumber: string;
  documentType: string;
  filingDate: string;
  borrower: string;  // Party1 = Grantor/Borrower
  lender: string;    // Party2 = Grantee/Lender
  legalDescription: string;
}

interface ImportResult {
  totalParsed: number;
  corporateLenderFiltered: number;
  familyFiltered: number;
  excludedFiltered: number;
  existingSkipped: number;
  imported: number;
}

// Corporate keywords — if the Lender (Party2) does NOT match any of these,
// it's likely an individual lender = potential owner finance
const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'DHI', 'USAA', 'NAVY FEDERAL',
  'VETERANS UNITED', 'GUILD', 'CHURCHILL', 'FAIRWAY', 'NEWREZ',
  'FLAGSTAR', 'FIDELITY', 'FIRST NATIONAL', 'FIRST FINANCIAL',
  'ROCKET MORTGAGE', 'LOANDEPOT', 'MR COOPER', 'PRIMELENDING',
  'QUICKEN LOANS', 'FREEDOM MORTGAGE', 'AMERISAVE',
  'GUARANTEED RATE', 'HOME POINT', 'CARDINAL FINANCIAL',
  'NATIONS LENDING', 'GATEWAY MORTGAGE', 'JPMORGAN', 'BANCORPSOUTH',
  'MOVEMENT MORTGAGE', 'CROSSCOUNTRY MORTGAGE', 'ACADEMY MORTGAGE',
  'PROSPERITY HOME', 'PULTE MORTGAGE', 'AMERIHOME',
  'FIRST HORIZON', 'BETTER MORTGAGE', 'CALIBER HOME',
  'SECRETARY OF HOUSING', 'HUD', 'HOUSING AND URBAN',
  'TEXAS STATE AFFORDABLE', 'TEXAS REPUBLIC',
  'CARRINGTON MORTGAGE', 'ROUNDPOINT MORTGAGE',
  'BROKER SOLUTIONS', 'CERTAINTY HOME', 'CRYSTAL CLEAR MORTGAGE',
  'HOMETOWN EQUITY', 'HOMETOWN LENDERS', 'AMERICAN FINANCING',
  'FIRST GUARANTY MORTGAGE', 'GUARDIAN MORTGAGE', 'NATIONSTAR',
  'ORIGIN BANK', 'RANDOLPH-BROOKS', 'VERITEX', 'FLAGSTAR BANK',
  'SOMBRERO PROPERTY', 'EQUITY TRUST', 'BOSSE LENDING',
  'FIRST FEDERAL', 'HERITAGE LAND BANK', 'LONE STAR',
  'FARM CREDIT', 'AGTEXAS', 'PLAINS LAND BANK',
  'AMERICAN AIRLINES FEDERAL', 'FIRST UNITED BANK',
  'LEGEND BANK', 'FANNIN BANK', 'CADENCE BANK', 'PROSPERITY BANK',
  'ENNIS STATE BANK', 'F&M BANK', 'CITIZENS BANK', 'INDEPENDENT BANK',
  'GUARANTY BANK', 'FIRST STATE BANK', 'AMERICAN NATIONAL BANK',
  'TEXAS REPUBLIC BANK', 'US BANK', 'FIRST COMMUNITY MORTGAGE',
  'TOWN SQUARE MORTGAGE', 'GENEVA FINANCIAL', 'AMCAP MORTGAGE',
  'VIP MORTGAGE', 'GUILD MORTGAGE', 'LOAN SIMPLE',
  'AMERICAN FINANCIAL NETWORK', 'PROVIDENT TRUST',
  '21ST MORTGAGE', 'RCN CAPITAL', 'LOWER LLC',
  'BOKF NA', 'W V T SERVICE', 'EUSTIS MORTGAGE',
  'SOUTHWEST STAGE FUNDING', 'CERTAINTY HOME LOANS',
  'CHURCH', 'PARISH', 'MINISTRY', 'MINISTRIES',
];

const extractLastName = (name: string): string => {
  // Remove ET UX/ET VIR/ET AL/JR/SR/II/III suffixes for comparison
  const cleaned = name.trim().toUpperCase()
    .replace(/\s+ET\s+(UX|VIR|AL)\b/g, '')
    .replace(/\s+(JR|SR|II|III|IV)\b/g, '')
    .trim();
  const parts = cleaned.split(/\s+/);
  return parts[0] || '';
};

const isCorporateLender = (name: string): boolean => {
  const upper = name.toUpperCase();
  return CORPORATE_KEYWORDS.some(kw => {
    const regex = new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(upper);
  });
};

const isFamilyTransfer = (party1: string, party2: string): boolean => {
  if (isCorporateLender(party1) || isCorporateLender(party2)) return false;
  const last1 = extractLastName(party1);
  const last2 = extractLastName(party2);
  return last1 === last2 && last1.length > 1;
};

/**
 * Split the remainder (after AM/PM) into borrower, lender, and legal description.
 * Legal starts with "Sub:" pattern. Names are separated by known boundaries.
 */
const splitPartiesAndLegal = (text: string): { borrower: string; lender: string; legal: string } => {
  let namesPart = text;
  let legal = '';

  // Extract legal description (starts with "Sub:")
  const subMatch = text.match(/Sub:\s*.+$/);
  if (subMatch && subMatch.index !== undefined) {
    namesPart = text.substring(0, subMatch.index).trim();
    legal = subMatch[0].trim();
  }

  // Try tab separation first
  if (namesPart.includes('\t')) {
    const parts = namesPart.split('\t').filter(Boolean);
    return { borrower: parts[0]?.trim() || '', lender: parts.slice(1).join(' ').trim(), legal };
  }

  // Try double-space separation
  const spaceParts = namesPart.split(/\s{2,}/).filter(Boolean);
  if (spaceParts.length >= 2) {
    return { borrower: spaceParts[0].trim(), lender: spaceParts.slice(1).join(' ').trim(), legal };
  }

  // Look for ET UX/ET VIR/ET AL as a boundary marker
  // After "ET UX" or "ET VIR" or "ET AL" the next ALL-CAPS word starts the lender
  const etMatch = namesPart.match(/^(.+?\bET\s+(?:UX|VIR|AL)\b)\s*(.+)$/i);
  if (etMatch) {
    return { borrower: etMatch[1].trim(), lender: etMatch[2].trim(), legal };
  }

  // Try entity boundary (LLC, INC, CORPORATION, BANK, NA, DBA at end of lender)
  const entityPatterns = [
    /\b(LLC|INC|CORPORATION|COMPANY|LTD|BANK\s+NA|BANK\s+FSB|BANK|NA|DBA)\b/gi,
  ];
  for (const pattern of entityPatterns) {
    const matches = [...namesPart.matchAll(pattern)];
    if (matches.length >= 1) {
      // Find first entity keyword — everything before a name that precedes the entity is the borrower
      // Actually, we need to find where the borrower ends and the lender begins
      // The lender typically has the entity keyword
      // Strategy: find the last personal name before the first entity keyword
      const firstEntity = matches[0];
      if (firstEntity.index && firstEntity.index > 3) {
        // Check if entity is inside the borrower name or the lender
        // If there are multiple entity matches, the first one might be in lender
        // Try splitting at the point just before a known corporate keyword
        for (const kw of CORPORATE_KEYWORDS) {
          const kwIdx = namesPart.toUpperCase().indexOf(kw);
          if (kwIdx > 3 && kwIdx < namesPart.length - 2) {
            return {
              borrower: namesPart.substring(0, kwIdx).trim(),
              lender: namesPart.substring(kwIdx).trim(),
              legal,
            };
          }
        }
      }
    }
  }

  // Last resort: suffix-based split (like Antrim)
  const suffixSplit = namesPart.match(
    /^(.+?\b(?:[A-Z]|JR|SR|II|III|IV)\b)\s*([A-Z][A-Z]+\s.+)$/
  );
  if (suffixSplit) {
    return { borrower: suffixSplit[1].trim(), lender: suffixSplit[2].trim(), legal };
  }

  return { borrower: namesPart.trim(), lender: '', legal };
};

export function FanninCountyTextParser({ onRecordsSaved }: FanninCountyTextParserProps) {
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const parseFanninText = (text: string): ParsedRecord[] => {
    const records: ParsedRecord[] = [];
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

    // === Try concatenated single-line format first ===
    // e.g. "2022000783DEED OF TRUST1/31/2022 3:43:23 PMSAENZ JR FELIX RBOURLAND RICHARD E ET UX"
    for (const line of lines) {
      const match = line.match(
        /^(\d{7,15})DEED OF TRUST\s*(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{1,2}:\d{2}:\d{2})\s+(AM|PM)(.+)$/i
      );
      if (match) {
        const documentNumber = match[1];
        const dateStr = match[2];
        const remainder = match[5].trim();
        const dateParts = dateStr.split('/');
        const filingDate = dateParts.length === 3
          ? `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`
          : dateStr;
        const { borrower, lender, legal } = splitPartiesAndLegal(remainder);
        if (borrower && documentNumber) {
          records.push({ documentNumber, documentType: 'DEED OF TRUST', filingDate, borrower, lender, legalDescription: legal });
        }
      }
    }

    if (records.length > 0) {
      console.log(`Fannin parser (single-line): found ${records.length} records`);
      return records;
    }

    // === Multi-line format (from "Printer Friendly" view) ===
    // Pattern:
    //   {docNumber}          ← line starting with digits
    //   DEED OF TRUST        ← doc type line
    //   {date} {time}        ← date line
    //   {Borrower}           ← borrower name (one or more lines)
    //   {Lender}             ← lender name (one or more lines)
    //   [Sub: ...]           ← optional legal description
    // Then next record starts with another digit line

    let i = 0;
    while (i < lines.length) {
      // Find a document number line (7-15 digits)
      const docMatch = lines[i].match(/^(\d{7,15})$/);
      if (!docMatch) { i++; continue; }

      const documentNumber = docMatch[1];
      i++;

      // Expect "DEED OF TRUST" (skip if not)
      if (i >= lines.length || !lines[i].toUpperCase().includes('DEED OF TRUST')) { continue; }
      i++;

      // Expect date line
      if (i >= lines.length) break;
      const dateMatch = lines[i].match(/^(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{1,2}:\d{2}:\d{2}\s+(?:AM|PM))/i);
      if (!dateMatch) { continue; }
      const dateStr = dateMatch[1];
      const dateParts = dateStr.split('/');
      const filingDate = dateParts.length === 3
        ? `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`
        : dateStr;
      i++;

      // Collect borrower lines until we hit the next doc number, "Sub:", "DEED OF TRUST", or date pattern
      const borrowerLines: string[] = [];
      const lenderLines: string[] = [];
      let legal = '';

      // Helper: is this line a new record boundary?
      const isBoundary = (line: string) =>
        /^\d{7,15}$/.test(line) || /^DEED OF TRUST$/i.test(line);
      const isDate = (line: string) =>
        /^\d{1,2}\/\d{1,2}\/\d{4}/.test(line);
      const isLegal = (line: string) =>
        /^Sub:\s/i.test(line);

      // Read borrower name lines — we know the borrower comes first
      // We'll collect ALL name lines then split them: the "lender" portion 
      // is determined by the corporate filter or by count
      const allNameLines: string[] = [];
      while (i < lines.length && !isBoundary(lines[i]) && !isDate(lines[i])) {
        if (isLegal(lines[i])) {
          legal = lines[i];
          i++;
          continue;
        }
        allNameLines.push(lines[i]);
        i++;
      }

      // Split name lines: borrower is typically 1 line (or 2 with ET UX spouse), lender is the rest
      // Strategy: first line(s) are borrower until we see a line that looks like a different entity
      if (allNameLines.length >= 2) {
        // Simple heuristic: first line is borrower, rest is lender
        // But if first line has "ET UX" / "ET VIR" / "ET AL", the next line might still be borrower spouse
        let splitIdx = 1;
        if (allNameLines[0].match(/\bET\s+(UX|VIR|AL)\b/i) && allNameLines.length >= 3) {
          // Check if line 1 looks like it could be a spouse (same last name)
          const firstLast = extractLastName(allNameLines[0]);
          const secondLast = extractLastName(allNameLines[1]);
          if (firstLast === secondLast && firstLast.length > 1) {
            splitIdx = 2; // Include spouse in borrower
          }
        }
        const borrower = allNameLines.slice(0, splitIdx).join('; ');
        const lender = allNameLines.slice(splitIdx).join('; ');
        
        if (borrower && documentNumber) {
          records.push({ documentNumber, documentType: 'DEED OF TRUST', filingDate, borrower, lender, legalDescription: legal });
        }
      } else if (allNameLines.length === 1) {
        records.push({ documentNumber, documentType: 'DEED OF TRUST', filingDate, borrower: allNameLines[0], lender: '', legalDescription: legal });
      }
    }

    console.log(`Fannin parser (multi-line): found ${records.length} records`);
    return records;
  };

  const processRecords = async () => {
    if (!rawText.trim()) {
      toast({ title: "No data", description: "Please paste Fannin County data to import", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const allRecords = parseFanninText(rawText);
      setProgress(20);

      if (allRecords.length === 0) {
        toast({
          title: "No records found",
          description: "Could not parse any DEED OF TRUST records. Make sure you're pasting rows from the Fannin County search results.",
          variant: "destructive"
        });
        setIsProcessing(false);
        return;
      }

      const result: ImportResult = {
        totalParsed: allRecords.length,
        corporateLenderFiltered: 0,
        familyFiltered: 0,
        excludedFiltered: 0,
        existingSkipped: 0,
        imported: 0,
      };

      // DOT filtering: keep only records where the Lender (Party2) is NOT a corporate entity
      const validRecords: ParsedRecord[] = [];
      for (const rec of allRecords) {
        // If lender is corporate → standard bank deal, skip
        if (rec.lender && isCorporateLender(rec.lender)) {
          result.corporateLenderFiltered++;
          continue;
        }
        // Family transfer check
        if (rec.lender && isFamilyTransfer(rec.borrower, rec.lender)) {
          result.familyFiltered++;
          continue;
        }
        // No lender parsed — can't verify, skip
        if (!rec.lender) {
          result.excludedFiltered++;
          continue;
        }
        validRecords.push(rec);
      }
      setProgress(50);

      // Check existing
      const docNumbers = validRecords.map(r => r.documentNumber);
      const { data: existing } = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'fannin')
        .in('document_number', docNumbers.length > 0 ? docNumbers : ['__none__']);

      const existingSet = new Set(existing?.map(e => e.document_number) || []);
      setProgress(60);

      const recordsToInsert = validRecords.filter(r => {
        if (existingSet.has(r.documentNumber)) {
          result.existingSkipped++;
          return false;
        }
        return true;
      });

      if (recordsToInsert.length > 0) {
        // DOT mapping: Borrower → grantor_name, Lender → grantee_name
        const insertData = recordsToInsert.map(rec => ({
          county: 'fannin',
          document_number: rec.documentNumber,
          document_type: rec.documentType,
          filing_date: rec.filingDate,
          grantor_name: rec.borrower,      // Borrower
          grantee_name: rec.lender || null, // Lender (individual = owner finance lead)
          legal_description: rec.legalDescription || null,
          is_potential_owner_finance: true,
          confidence_score: 85,
          detection_reasons: JSON.stringify(['Deed of Trust', 'Individual Lender', 'Fannin County TX']),
          search_date: new Date().toISOString(),
          status: 'new',
        }));

        const batchSize = 50;
        for (let i = 0; i < insertData.length; i += batchSize) {
          const batch = insertData.slice(i, i + batchSize);
          const { error } = await supabase.from('county_research').insert(batch);
          if (error) {
            console.error('Insert error:', error);
            throw error;
          }
          result.imported += batch.length;
          setProgress(60 + ((i + batch.length) / insertData.length) * 35);
        }
        backupRecordsToExcel(insertData, 'fannin', 'fannin_parser');
      }

      setProgress(100);
      setImportResult(result);
      setRawText("");

      toast({
        title: "Import complete",
        description: `Imported ${result.imported} owner-financed records from Fannin County`,
      });

      if (onRecordsSaved) onRecordsSaved();
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setRawText(text);
    toast({ title: "File loaded", description: `Loaded ${file.name} - ${text.split('\n').length} lines` });
  };

  const previewParse = () => {
    const records = parseFanninText(rawText);
    const ownerFinanced = records.filter(r => r.lender && !isCorporateLender(r.lender));
    console.log('Preview parse results:', records);
    console.log('Owner-financed (individual lender):', ownerFinanced);
    toast({
      title: `Preview: ${records.length} total, ${ownerFinanced.length} potential owner-financed`,
      description: ownerFinanced.length > 0
        ? `First: ${ownerFinanced[0].borrower} → ${ownerFinanced[0].lender}`
        : records.length > 0
          ? 'All records have corporate lenders — none are owner-financed.'
          : 'No records could be parsed. Check console for details.',
    });
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert className={importResult.imported > 0 ? "border-green-500" : "border-yellow-500"}>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Import Results</AlertTitle>
          <AlertDescription>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>Total DOTs Parsed: <strong>{importResult.totalParsed}</strong></div>
              <div>Corporate Lenders Filtered: <strong>{importResult.corporateLenderFiltered}</strong></div>
              <div>Family Transfers Filtered: <strong>{importResult.familyFiltered}</strong></div>
              <div>No Lender / Excluded: <strong>{importResult.excludedFiltered}</strong></div>
              <div>Already in Database: <strong>{importResult.existingSkipped}</strong></div>
              <div className="text-green-600">Owner-Financed Imported: <strong>{importResult.imported}</strong></div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Fannin County, TX — Deed of Trust Parser
          </CardTitle>
          <CardDescription>
            Paste search results from the Fannin County Clerk website.
            Party 1 = Borrower (Grantor), Party 2 = Lender (Grantee/Beneficiary).
            Only records with <strong>individual lenders</strong> (not banks/mortgage companies) are imported as potential owner-finance deals.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Upload Text File
                <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
              </label>
            </Button>
            <Button variant="outline" onClick={() => setRawText("")} disabled={!rawText}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
            <Button variant="secondary" onClick={previewParse} disabled={!rawText.trim()}>
              Preview Parse
            </Button>
          </div>

          <Textarea
            placeholder={`Paste Fannin County DEED OF TRUST search results here...

Copy all rows from the search results table. Each line should contain:
Document No + DEED OF TRUST + Recorded Date + Borrower + Lender + Legals

Example:
2022000783DEED OF TRUST1/31/2022 3:43:23 PMSAENZ JR FELIX RBOURLAND RICHARD E ET UX
2022000597DEED OF TRUST1/25/2022 8:53:46 AMSAGE GROVE INVESTMENTS LLCEVANS II HAROLD M ET ALSub: SAGE GROVE ADDITION 1 Lot: 3`}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            className="min-h-[300px] font-mono text-xs"
          />

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing... {Math.round(progress)}%
              </p>
            </div>
          )}

          <Button onClick={processRecords} disabled={isProcessing || !rawText.trim()} className="w-full">
            {isProcessing ? "Processing..." : "Parse & Import Owner-Financed DOTs"}
          </Button>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>DOT Filtering Rules</AlertTitle>
        <AlertDescription className="text-xs space-y-1">
          <p>• <strong>Party 1</strong> = Borrower (Grantor) — the property owner</p>
          <p>• <strong>Party 2</strong> = Lender (Grantee/Beneficiary) — the person we want to contact</p>
          <p>• <strong>Corporate lenders</strong>: Banks, mortgage companies, LLCs → filtered out (standard institutional deals)</p>
          <p>• <strong>Individual lenders</strong>: Person-to-person financing → KEPT as owner-finance leads</p>
          <p>• <strong>Family transfers</strong>: Same last name on both sides → filtered out</p>
          <p>• <strong>Duplicates</strong>: Existing document numbers in database are skipped</p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
